/**
 * Format an ISO-8601 timestamp as a short German relative time:
 *   - "gerade eben"  (< 60 s)
 *   - "vor 4 Min"
 *   - "vor 2 Std"
 *   - "vor 3 Tagen"
 *   - "vor 5 Wochen"
 *   - "vor 3 Monaten"
 *   - else absolute YYYY-MM-DD
 *
 * Designed for the admin users table where the metadata line needs to stay
 * short and parseable at a glance — full timestamps eat width and force
 * line wraps.
 */
export function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return "—";
  const diffMs = Date.now() - ts;
  if (diffMs < 0) return new Date(iso).toISOString().slice(0, 10);
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return "gerade eben";
  if (min < 60) return `vor ${min} Min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `vor ${hr} Std`;
  const days = Math.floor(hr / 24);
  if (days === 1) return "gestern";
  if (days < 7) return `vor ${days} Tagen`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return weeks === 1 ? "vor 1 Woche" : `vor ${weeks} Wochen`;
  const months = Math.floor(days / 30);
  if (months < 12) return months === 1 ? "vor 1 Monat" : `vor ${months} Monaten`;
  // Older than a year — fall back to the absolute date so we don't bury it
  // behind a stale "vor 14 Monaten".
  return new Date(iso).toISOString().slice(0, 10);
}

/** Short absolute date for the "Registriert" stamp — "13.05.26". */
export function shortDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yy = String(d.getUTCFullYear() % 100).padStart(2, "0");
  return `${dd}.${mm}.${yy}`;
}
